'use client';
import { FEEDBACK_FILTER_VALUE } from '@/constants/bot';
import { useGetThreadDetails } from '@/hooks/useBot';
import { IThread, IThreadInteraction } from '@/types';
import { sortArray } from '@/utils';
import dayjs from 'dayjs';
import { Dispatch, FC, ReactNode, SetStateAction, createContext, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
const DEFAULT_SEARCH = 'Search anything';

interface IProductsContext {
  botIcon: string | null;
  userIcon: string | null;
  threads: IThread[];
  threadsData: any;
  spamCount: number;
  inboxCount: number;
  doneCount: number;
  setThreads: Dispatch<SetStateAction<IThread[]>>;
  activeThread: IThread | undefined;
  setActiveThread: Dispatch<SetStateAction<IThread | undefined>>;
  isLoading: boolean;
  hasLoadError: boolean;
  reloadThreads: () => void;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  threadInteractions: IThreadInteraction[];
  setThreadInteractions: Dispatch<SetStateAction<IThreadInteraction[]>>;
  handleSetDefaultThread: () => void;
  setFilter: Dispatch<SetStateAction<any>>;
  filter: any;
  feedbacksFilter: number[];
  setFeedbacksFilter: Dispatch<SetStateAction<number[]>>;
  token: string;
}

interface IProductsContextProviderProps {
  children: ReactNode;
  token: string;
}

export const ProductsContext = createContext<IProductsContext>({} as IProductsContext);

const ProductsContextProvider: FC<IProductsContextProviderProps> = ({ children, token }) => {
  const [filter, setFilter] = useState<any>({
    days: 7,
    view: 'inbox',
  });
  const [feedbacksFilter, setFeedbacksFilter] = useState<number[]>([FEEDBACK_FILTER_VALUE.ALL]);
  const [threads, setThreads] = useState<IThread[]>([]);
  const [activeThread, setActiveThread] = useState<IThread | undefined>();
  const [threadInteractions, setThreadInteractions] = useState<IThreadInteraction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [botIcon, setBotIcon] = useState(null);
  const [userIcon, setUserIcon] = useState(null);
  const [threadsData, setThreadsData] = useState<IThread[]>([]);
  const [spamCount, setSpamCount] = useState(0);
  const [inboxCount, setInboxCount] = useState(0);
  const [doneCount, setDoneCount] = useState(0);

  const {
    data: threadDetailsData,
    isLoading: isLoadingThreads,
    isError: hasLoadError,
    refetch: refetchThreads,
  } = useGetThreadDetails(token, filter);

  // Only show the skeleton while the active mailbox query has no data yet.
  useEffect(() => {
    setIsLoading(isLoadingThreads);
  }, [isLoadingThreads]);

  const getThreadData = () => {
    const normalizedThreads =
      threadDetailsData?.threads?.map((thread: any) => {
        const interactions = thread?.interactions || [];
        const lastInteraction = interactions.length ? interactions[interactions.length - 1] : null;
        const createdDate = thread?.created_date || thread?.date;
        const lastActivity = lastInteraction?.timestamp || createdDate || dayjs().toString();

        return {
          ...thread,
          created_date: createdDate,
          firstSeen: createdDate,
          date: lastActivity,
          lastInteractionAt: lastActivity,
        };
      }) || [];

    setThreadsData(normalizedThreads);
    setBotIcon(threadDetailsData?.botIcon || null);
    setUserIcon(threadDetailsData?.userIcon || null);
    setSpamCount(threadDetailsData?.spamCount || 0);
    setInboxCount(threadDetailsData?.inboxCount ?? normalizedThreads.length);
    setDoneCount(threadDetailsData?.doneCount || 0);
  };

  useEffect(() => {
    if (threadDetailsData) getThreadData();
  }, [threadDetailsData]);

  // Track previous thread ID to avoid resetting interactions on same-thread refetch
  const prevActiveThreadIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!activeThread) {
      prevActiveThreadIdRef.current = undefined;
      return;
    }
    const interactions = activeThread.interactions;
    if (interactions === undefined) return;

    if (activeThread.threadId !== prevActiveThreadIdRef.current) {
      // Thread switch: replace interactions with API data
      prevActiveThreadIdRef.current = activeThread.threadId;
      setThreadInteractions(interactions);
    }
    // Same thread refetch: socket events (bot:visitor_message, bot:ai_responded) keep
    // threadInteractions current — don't reset or we lose in-flight socket messages
  }, [activeThread, token]);

  const prevTokenRef = useRef(token);
  useEffect(() => {
    if (prevTokenRef.current !== token) {
      prevTokenRef.current = token;
      prevActiveThreadIdRef.current = undefined;
      setThreads([]);
      setActiveThread(undefined);
      setThreadInteractions([]);
      setThreadsData([]);
      setSpamCount(0);
      setInboxCount(0);
      setDoneCount(0);
      return;
    }
    handleInitThreads();
  }, [threadsData, token]);

  const handleInitThreads = () => {
    if (!threadsData?.length) {
      setThreads([]);
      setActiveThread(undefined);
      setThreadInteractions([]);
      return;
    }
    const sortedThreads = sortArray([...threadsData], {
      isSortByDate: true,
      isAscending: false,
      sortBy: 'date',
    });
    setThreads(sortedThreads);
    // Preserve the user's current thread selection; only auto-select first on initial load
    setActiveThread((current) => {
      if (current?.threadId) {
        const updated = sortedThreads.find((t) => t.threadId === current.threadId);
        if (updated) return updated; // Refresh thread data but keep same thread selected
      }
      return sortedThreads[0];
    });
  };

  const handleSetDefaultThread = () => {
    const defaultThread = {
      threadId: uuidv4(),
      title: DEFAULT_SEARCH,
      status: '',
      date: dayjs().toString(),
      isInitialThread: true,
    };
    setThreads([defaultThread]);
    setActiveThread(defaultThread);
  };

  return (
    <ProductsContext.Provider
      value={{
        botIcon,
        userIcon,
        threads,
        setThreads,
        activeThread,
        setActiveThread,
        threadsData,
        spamCount,
        inboxCount,
        doneCount,
        isLoading,
        hasLoadError,
        reloadThreads: () => { void refetchThreads(); },
        setIsLoading,
        threadInteractions: sortArray(threadInteractions, {
          sortBy: 'id',
          isAscending: true,
        }),
        setThreadInteractions,
        handleSetDefaultThread,
        setFilter,
        filter,
        feedbacksFilter,
        setFeedbacksFilter,
        token,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export default ProductsContextProvider;
